import React, { type ComponentType } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import withContainerWidth from '@atlassian/jira-issue-view-common-utils/src/with-container-width/index.tsx';
import {
	SectionHeading,
	SectionHeadingIcons,
} from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { hasExceededIssuesLimitInitialLoad } from '../../common/utils/has-exceeded-issues-limit/index.tsx';
import type { AddChildEventHandler } from './add-child/types.tsx';

type ConnectProps = {
	Title: ComponentType<{
		hasMultipleChildPanels?: boolean;
		childIssuesLimitStatus: ChildIssuesLimitStatus;
		projectType?: string | null;
		isSimplified?: boolean | null;
		childIssuesHierarchyLevel?: number | null;
	}>;
	OldSectionHeadingIcons: ComponentType<{
		childIssuesLimitStatus: ChildIssuesLimitStatus;
		onAddChildClick: AddChildEventHandler | undefined;
		issueHierarchyLevel?: number;
		containerWidth?: number;
	}>;
	HeaderSectionHeadingIcons: ComponentType<{
		childIssuesLimitStatus: ChildIssuesLimitStatus;
		issueHierarchyLevel?: number | undefined;
		onAddChildClick: AddChildEventHandler | undefined;
		isHideDoneFilterEnabled: boolean | null | undefined;
		userPreferencesConnectionId: string | undefined;
		// TODO: Make required on hide-rank-option-when-unavailable cleanup
		isRankingEnabled?: boolean;
		columnPickerRef?: RefObject<HTMLDivElement>;
	}>;
};

type ParentProps = {
	onAddChildClick: AddChildEventHandler | undefined;
	sourceName?: string;
	childIssuesLimitStatus: ChildIssuesLimitStatus;
	issueHierarchyLevel: number | undefined;
	containerWidth: number;
	isHideDoneFilterEnabled: boolean | undefined | null;
	userPreferencesConnectionId: string | undefined;
	// TODO: Make required on hide-rank-option-when-unavailable cleanup
	isRankingEnabled?: boolean;
	columnPickerRef?: RefObject<HTMLDivElement>;
	hasMultipleChildPanels?: boolean;
	projectType?: string | null;
	isSimplified?: boolean | null;
	childIssuesHierarchyLevel?: number | null;
};

export type Props = ConnectProps & ParentProps;
export const sectionHeadingIconsTestId =
	'issue-view-common-views.child-issues-panel.heading.section-heading-icons';
export const oldSectionHeadingTestId =
	'issue-view-common-views.child-issues-panel.heading.old-section-heading';

function Heading(props: Props) {
	const {
		Title,
		OldSectionHeadingIcons,
		HeaderSectionHeadingIcons,
		onAddChildClick,
		sourceName,
		childIssuesLimitStatus,
		issueHierarchyLevel,
		containerWidth,
		isHideDoneFilterEnabled,
		userPreferencesConnectionId,
		isRankingEnabled,
		columnPickerRef,
		hasMultipleChildPanels,
		projectType,
		isSimplified,
		childIssuesHierarchyLevel,
	} = props;

	const ConfigurableChildIssueHeading = () => {
		return (
			<Box
				xcss={
					fg('magma-3191-render-chin-skeleton-in-ssr')
						? [marginBottomStyles, minHeightStyles]
						: marginBottomStyles
				}
				test-id={sectionHeadingIconsTestId}
			>
				<SectionHeading>
					{}
					<label htmlFor={sourceName}>
						<Title
							hasMultipleChildPanels={hasMultipleChildPanels}
							childIssuesLimitStatus={childIssuesLimitStatus}
							projectType={projectType}
							isSimplified={isSimplified}
							childIssuesHierarchyLevel={childIssuesHierarchyLevel}
						/>
					</label>
					{hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus) &&
					fg('jiv-chin-child-issue-limit-panel-header-enablement') ? null : (
						<HeaderSectionHeadingIcons
							columnPickerRef={columnPickerRef}
							onAddChildClick={onAddChildClick}
							childIssuesLimitStatus={childIssuesLimitStatus}
							issueHierarchyLevel={issueHierarchyLevel}
							isRankingEnabled={
								fg('hide-rank-option-when-unavailable') ? isRankingEnabled : undefined
							}
							userPreferencesConnectionId={userPreferencesConnectionId}
							isHideDoneFilterEnabled={isHideDoneFilterEnabled}
						/>
					)}
				</SectionHeading>
			</Box>
		);
	};

	const OldChildIssueHeading = () => {
		return (
			<SectionHeading test-id={oldSectionHeadingTestId}>
				{}
				<label htmlFor={sourceName}>
					<Title
						hasMultipleChildPanels={hasMultipleChildPanels}
						childIssuesLimitStatus={childIssuesLimitStatus}
					/>
				</label>
				<SectionHeadingIcons>
					<OldSectionHeadingIcons
						childIssuesLimitStatus={childIssuesLimitStatus}
						onAddChildClick={onAddChildClick}
						issueHierarchyLevel={issueHierarchyLevel}
						containerWidth={containerWidth}
					/>
				</SectionHeadingIcons>
			</SectionHeading>
		);
	};

	return expVal('jira-issue-view-chin-ga', 'isChildIssueEnabled', false)
		? ConfigurableChildIssueHeading()
		: OldChildIssueHeading();
}

export default withContainerWidth(Heading);

const marginBottomStyles = xcss({
	marginBottom: 'space.100',
});

// This minHeight is used to ensure that the heading does not jump when the meatball menu appears
const minHeightStyles = xcss({
	minHeight: '24px',
});
